// src/pages/gisung_gye/GisungGenerate.jsx
import React, { useState, useEffect, useRef } from "react";
import api from "../../api";

// ── 한글 금액 변환 ─────────────────────────────────────────────
function numberToKorean(num) {
  num = Math.floor(Number(num) || 0);
  if (num === 0) return "금 영원정";

  const units = ["", "만", "억", "조"];
  const small = ["", "일", "이", "삼", "사", "오", "육", "칠", "팔", "구"];
  const pos   = ["", "십", "백", "천"];

  let str = "";
  let ui  = 0;
  let n   = num;

  while (n > 0) {
    const chunk = n % 10000;
    if (chunk > 0) {
      let part = "";
      const d = [
        Math.floor(chunk / 1000),
        Math.floor((chunk % 1000) / 100),
        Math.floor((chunk % 100) / 10),
        chunk % 10,
      ];
      d.forEach((v, i) => {
        if (v === 0) return;
        part += small[v] + pos[3 - i];
      });
      str = part + units[ui] + str;
    }
    n = Math.floor(n / 10000);
    ui++;
  }

  return `금 ${str}원정`;
}

// ── 날짜 헬퍼 ─────────────────────────────────────────────────
const firstDay = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};
const lastDay = () => {
  const d = new Date();
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, "0")}-${String(last.getDate()).padStart(2, "0")}`;
};

// ── 메인 컴포넌트 ─────────────────────────────────────────────
export default function GisungGenerate({ type }) {
  const isGuchung = type === "구청";
  const color     = isGuchung ? "#1565C0" : "#2E7D32";
  const label     = isGuchung ? "구청 등" : "공단";
  const sheets    = isGuchung
    ? ["기성계", "청구서", "기성검사원"]
    : ["기성금청구서", "기성부분 감독조서", "계좌이체 의뢰서"];

  const [contracts,        setContracts]        = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);
  const [prevTotal,        setPrevTotal]        = useState(0);
  const [prevLast,         setPrevLast]         = useState(0);  // 바로 직전 회차 금액


  const [prevRecords,      setPrevRecords]      = useState([]);
  const [loading,          setLoading]          = useState(false);
  const [error,            setError]            = useState("");
  const [success,          setSuccess]          = useState("");
  const [showModal,        setShowModal]        = useState(false);

  const [form, setForm] = useState({
    contract:             "",
    gisung_year:          new Date().getFullYear(),
    gisung_month:         new Date().getMonth() + 1,
    service_start:        firstDay(),
    service_end:          lastDay(),
    bill_date:            lastDay(),
    current_amount_text:  "",
    current_amount:       "",
    prev_amount_text:     "",
    prev_amount:          "",
    remaining_text:       "",
    remaining_amount:     "",
  });

  const skipCurrentEffect = useRef(false);

  // ── 계약 목록 로드 ─────────────────────────────────────────
  useEffect(() => {
    api
      .get("gisung-gye/contracts/", {
        params: { type: type === "구청" ? "구청" : "공단" },
      })
      .then(res => setContracts(res.data));
  }, [type]);

  // ── 계약 선택 시: 이전 기성 기록 조회 ──────────────────────
  useEffect(() => {
    if (!selectedContract) return;

    api
      .get("gisung-gye/records/", { params: { contract: selectedContract.id } })
      .then(res => {
        const records = res.data;
        setPrevRecords(records);

        const total = records.reduce((s, r) => s + Number(r.current_amount), 0);
        setPrevTotal(total);
        
        // 바로 직전 회차 = gisung_round 가장 큰 record
        const lastRecord = records.reduce(
          (max, r) => (r.gisung_round > (max?.gisung_round ?? -1) ? r : max),
          null
        );
        const last = lastRecord ? Number(lastRecord.current_amount) : 0;
        setPrevLast(last);
        
        if (!isGuchung) {
          const cur       = Number(form.current_amount) || 0;
          const remaining = Number(selectedContract.contract_amount) - total - cur;
        
          skipCurrentEffect.current = true;
          setForm(p => ({
            ...p,
            prev_amount:      last,          // ← total → last (직전 회차만)
            prev_amount_text: numberToKorean(last),
            remaining_amount: remaining,
            remaining_text:   numberToKorean(remaining),
          }));
        }


      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContract]);

  // ── 금회기성금액 변경 시: 한글 자동변환 + 기성고잔액 재계산 ──
  useEffect(() => {
    if (skipCurrentEffect.current) {
      skipCurrentEffect.current = false;
      return;
    }

    const cur = Number(form.current_amount) || 0;

    setForm(p => ({ ...p, current_amount_text: numberToKorean(cur) }));

    if (!isGuchung && selectedContract) {
      const remaining = Number(selectedContract.contract_amount) - prevTotal - cur;
      setForm(p => ({
        ...p,
        current_amount_text: numberToKorean(cur),
        remaining_amount:    remaining,
        remaining_text:      numberToKorean(remaining),
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.current_amount]);

  // ── 이벤트 핸들러 ─────────────────────────────────────────
  const onChange = e =>
    setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const onContractChange = e => {
    const id = e.target.value;
    setForm(p => ({
      ...p,
      contract:            id,
      current_amount_text: "",
      current_amount:      "",
      prev_amount_text:    "",
      prev_amount:         "",
      remaining_text:      "",
      remaining_amount:    "",
    }));


    setSelectedContract(contracts.find(c => String(c.id) === id) || null);
    setPrevTotal(0);
    setPrevLast(0);
    setPrevRecords([]);


  };

  const onSubmit = async () => {
    setError(""); setSuccess(""); setLoading(true);
    try {
      const raw = { ...form };

      ["current_amount", "prev_amount", "remaining_amount"].forEach(k => {
        if (raw[k] === "") raw[k] = null;
      });

      if (isGuchung) {
        delete raw.prev_amount_text;
        delete raw.prev_amount;
        delete raw.remaining_text;
        delete raw.remaining_amount;
      }

      const res = await api.post("gisung-gye/generate/", raw, {
        responseType: "blob",
      });

      const url     = window.URL.createObjectURL(new Blob([res.data]));
      const a       = document.createElement("a");
      a.href        = url;
      const ym      = `${form.gisung_year}-${String(form.gisung_month).padStart(2, "0")}`;
      const svcName = selectedContract?.service_name || "용역";
      a.download    = `${ym}_기성계_${svcName}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);

      setSuccess(`✅ 기성계 파일이 생성되었습니다. (${sheets.join(" / ")})`);
      setShowModal(true);

    } catch (e) {
      if (e.response?.data instanceof Blob) {
        const text = await e.response.data.text();
        try { setError(JSON.parse(text)?.error || text); }
        catch { setError(text); }
      } else {
        setError(e.response?.data?.error || "오류가 발생했습니다.");
      }
    } finally {
      setLoading(false);
    }
  };

  // ── 회차 계산 ──────────────────────────────────────────────
  const gisung_round = selectedContract
    ? form.gisung_month - selectedContract.start_month + 1
    : null;

  // ── 렌더 ──────────────────────────────────────────────────
  return (
    <div style={S.page}>

      {/* 성공 모달 */}
      {showModal && (
        <div style={S.modalOverlay} onClick={() => setShowModal(false)}>
          <div style={S.modalBox} onClick={e => e.stopPropagation()}>
            <div style={S.modalIcon}>✅</div>
            <div style={S.modalTitle}>기성계 생성 완료!</div>
            <div style={S.modalSheets}>
              {sheets.map(s => (
                <span key={s} style={{ ...S.badge, background: color }}>{s}</span>
              ))}
            </div>
            <div style={S.modalMsg}>파일이 자동으로 다운로드되었습니다.</div>
            <div style={S.modalRefresh}>
              📋 이력 페이지에서 확인하려면 새로고침이 필요합니다.
            </div>
            <button
              style={{ ...S.generateBtn, background: color, marginTop: 20 }}
              onClick={() => setShowModal(false)}
            >
              확인
            </button>
          </div>
        </div>
      )}

      {/* 헤더 */}
      <div style={S.header}>
        <h2 style={S.title}>📄 기성계 생성 — {label}</h2>
        <div style={{ ...S.sheetRow, background: color + "18" }}>
          <span style={{ color, fontWeight: 700, fontSize: 13 }}>생성 시트 ▶</span>
          {sheets.map(s => (
            <span key={s} style={{ ...S.badge, background: color }}>{s}</span>
          ))}
        </div>
      </div>

      <div style={S.body}>

        {/* ── 왼쪽: 계약 선택 + 미리보기 ── */}
        <div style={S.leftCol}>
          <div style={S.card}>
            <div style={{ ...S.cardTitle, color }}>① 계약 선택</div>
            <select
              name="contract"
              value={form.contract}
              onChange={onContractChange}
              style={{ ...S.input, fontSize: 14 }}
            >
              <option value="">-- 계약을 선택하세요 --</option>
              {contracts.map(c => (
                <option key={c.id} value={c.id}>
                  [{c.department}] {c.institution} — {c.service_name}
                </option>
              ))}
            </select>

            {/* 계약 정보 미리보기 */}
            {selectedContract && (
              <div style={S.preview}>
                <div style={S.previewTitle}>계약 정보</div>
                {[
                  ["발주처/기관명", selectedContract.institution],
                  ["용역명",        selectedContract.service_name],
                  ["계약금액",      Number(selectedContract.contract_amount).toLocaleString() + "원"],
                  ["계약기간",      selectedContract.contract_period],
                  ["착수월",        selectedContract.start_month + "월"],
                  ...(isGuchung
                    ? [["귀중/귀하", selectedContract.recipient]]
                    : [
                        ["발주처명", selectedContract.ordering_dept],
                        ["위치",     selectedContract.location],
                        ["주소",     selectedContract.address],   // ← 추가

                        
                      ]),
                ].map(([k, v]) => (
                  <div key={k} style={S.previewRow}>
                    <span style={S.previewKey}>{k}</span>
                    <span>{v}</span>
                  </div>
                ))}
              </div>
            )}

            {/* ── 누적 현황: 구청/공단 공통 ── */}
            {selectedContract && (
              <div style={S.historyBox}>
                <div style={S.historyTitle}>📋 기성 누적 현황</div>
                {prevRecords.length === 0 ? (
                  <div style={S.historyEmpty}>첫 번째 회차 — 기기성 없음</div>
                ) : (
                  <>
                    <table style={S.historyTable}>
                      <thead>
                        <tr>
                          <th style={S.th}>회차</th>
                          <th style={S.th}>기성월</th>
                          <th style={{ ...S.th, textAlign: "right" }}>금회기성금액</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...prevRecords]
                          .sort((a, b) => a.gisung_round - b.gisung_round)
                          .map(r => (
                            <tr key={r.id}>
                              <td style={S.td}>{r.gisung_round}회차</td>
                              <td style={S.td}>
                                {r.gisung_year}.{String(r.gisung_month).padStart(2, "0")}
                              </td>
                              <td style={{ ...S.td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                                {Number(r.current_amount).toLocaleString()}원
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                    <div style={S.historyTotal}>
                      <span>누적 기기성합계</span>
                      <strong style={{ color }}>{prevTotal.toLocaleString()}원</strong>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── 자동 계산 공식: 구청/공단 공통 ── */}
            {selectedContract && (
              <div style={{ ...S.formulaBox, marginTop: 12 }}>
                <div style={S.formulaTitle}>📐 자동 계산 공식</div>
                {isGuchung ? (
                  <>
                    <div style={S.formulaRow}>
                      <span style={S.formulaLabel}>누계기성</span>
                      <span>= 이전 회차 금회기성금액 합계</span>
                      <strong style={{ color }}>= {prevTotal.toLocaleString()}원</strong>
                    </div>
                    <div style={S.formulaRow}>
                      <span style={S.formulaLabel}>잔액(참고)</span>
                      <span>= 계약금액 − 누계기성 − 금회기성</span>
                      <strong style={{ color }}>
                        = {(
                          Number(selectedContract.contract_amount) -
                          prevTotal -
                          (Number(form.current_amount) || 0)
                        ).toLocaleString()}원
                      </strong>
                    </div>
                  </>
                ) : (
                  <>


                    <div style={S.formulaRow}>
                      <span style={S.formulaLabel}>기기성금액</span>
                      <span>= 바로 직전 회차 기성금액</span>
                      <strong style={{ color }}>= {prevLast.toLocaleString()}원</strong>
                      </div>
                      <div style={S.formulaRow}>
                      <span style={S.formulaLabel}>기기성합계</span>
                      <span>= 1회차 ~ 직전 회차 누계</span>
                      <strong style={{ color }}>= {prevTotal.toLocaleString()}원</strong>
                      </div>
                      <div style={S.formulaRow}>
                      <span style={S.formulaLabel}>기성고잔액</span>
                      <span>= 계약금액 − 기기성합계 − 금회기성</span>
                      <strong style={{ color }}>
                        = {(
                          Number(selectedContract.contract_amount) -
                          prevTotal -
                          (Number(form.current_amount) || 0)
                        ).toLocaleString()}원
                      </strong>
                    </div>


                  </>
                )}
                <div style={S.formulaNote}>
                  {isGuchung
                    ? "※ 잔액은 참고용이며 엑셀에 자동 반영되지 않습니다."
                    : "※ 자동 계산 값이 맞지 않으면 직접 수정 가능합니다."}
                </div>
              </div>
            )}

          </div> {/* card */}
        </div>   {/* leftCol */}

        {/* ── 오른쪽: 기성 정보 입력 ── */}
        <div style={S.rightCol}>
          <div style={S.card}>
            <div style={{ ...S.cardTitle, color }}>② 기성 정보 입력</div>

            <div style={S.grid2}>
              <Field label="기성 연도">
                <input name="gisung_year" type="number"
                  value={form.gisung_year} onChange={onChange} style={S.input} />
              </Field>
              <Field label="기성 월">
                <input name="gisung_month" type="number" min={1} max={12}
                  value={form.gisung_month} onChange={onChange} style={S.input} />
              </Field>
              <Field label="용역 시작일">
                <input name="service_start" type="date"
                  value={form.service_start} onChange={onChange} style={S.input} />
              </Field>
              <Field label="용역 종료일">
                <input name="service_end" type="date"
                  value={form.service_end} onChange={onChange} style={S.input} />
              </Field>
              <Field label="청구일">
                <input name="bill_date" type="date"
                  value={form.bill_date} onChange={onChange} style={S.input} />
              </Field>
            </div>

            <div style={{ ...S.cardTitle, color, marginTop: 20 }}>③ 금액 입력</div>
            <div style={S.grid2}>
              <Field label={<AutoLabel>금회기성금액 (한글)</AutoLabel>}>
                <input
                  name="current_amount_text"
                  value={form.current_amount_text}
                  onChange={onChange}
                  style={{ ...S.input, ...S.autoInput }}
                  placeholder="자동 변환"
                />
              </Field>
              <Field label="금회기성금액 (숫자)">
                <input
                  name="current_amount"
                  type="number"
                  value={form.current_amount}
                  onChange={onChange}
                  style={S.input}
                />
              </Field>

              {/* 공단 전용: 자동 계산 필드 */}
              {!isGuchung && (
                <>
                  <Field label={<AutoLabel>기기성금액 (한글)</AutoLabel>}>
                    <input
                      name="prev_amount_text"
                      value={form.prev_amount_text}
                      onChange={onChange}
                      style={{ ...S.input, ...S.autoInput }}
                      placeholder="자동 계산"
                    />
                  </Field>

                  <Field label={<AutoLabel>기기성금액 (숫자)</AutoLabel>}>
                    <div style={S.autoWrap}>
                      <input
                        name="prev_amount"
                        type="text"
                        value={form.prev_amount !== "" ? Number(form.prev_amount).toLocaleString() : ""}
                        onChange={e => setForm(p => ({ ...p, prev_amount: e.target.value.replace(/,/g, "") }))}
                        style={{ ...S.input, ...S.autoInput }}
                        placeholder="자동 계산"
                      />
                      <span style={S.autoTag}>자동</span>
                    </div>
                  </Field>



                  <Field label={<AutoLabel>기성고잔액 (한글)</AutoLabel>}>
                    <input
                      name="remaining_text"
                      value={form.remaining_text}
                      onChange={onChange}
                      style={{ ...S.input, ...S.autoInput }}
                      placeholder="자동 계산"
                    />
                  </Field>

                  <Field label={<AutoLabel>기성고잔액 (숫자)</AutoLabel>}>
                    <div style={S.autoWrap}>
                      <input
                        name="remaining_amount"
                        type="text"
                        value={form.remaining_amount !== "" ? Number(form.remaining_amount).toLocaleString() : ""}
                        onChange={e => setForm(p => ({ ...p, remaining_amount: e.target.value.replace(/,/g, "") }))}
                        style={{ ...S.input, ...S.autoInput }}
                        placeholder="자동 계산"
                      />
                      <span style={S.autoTag}>자동</span>
                    </div>
                  </Field>


                </>
              )}
            </div>

            {/* 회차 미리보기 */}
            {selectedContract && form.gisung_month && (
              <div style={S.roundPreview}>
                기성 회차:&nbsp;
                <strong style={{ color }}>{gisung_round}회차</strong>
                {!isGuchung && prevRecords.length > 0 && (
                  <span style={{ marginLeft: 16, color: "#888", fontSize: 12 }}>
                    (이전 {prevRecords.length}회차 기성 내역 존재)
                  </span>
                )}
              </div>
            )}

            {error   && <div style={S.errorBox}>{error}</div>}
            {success && <div style={S.successBox}>{success}</div>}

            <button
              style={{ ...S.generateBtn, background: loading ? "#aaa" : color }}
              onClick={onSubmit}
              disabled={loading || !form.contract}
            >
              {loading ? "⏳ 생성 중..." : "📥 엑셀 파일 생성"}
            </button>
            <p style={S.hint}>생성 시 DB에 이력이 저장되며 자동으로 다운로드됩니다.</p>

          </div> {/* card */}
        </div>   {/* rightCol */}

      </div> 
    </div>  
  );
}

// ── 보조 컴포넌트 ──────────────────────────────────────────────
function Field({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: "#444" }}>{label}</label>
      {children}
    </div>
  );
}

function AutoLabel({ children }) {
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
      {children}
      <span style={{
        fontSize: 9, background: "#E8F5E9", color: "#2E7D32",
        borderRadius: 3, padding: "1px 4px", fontWeight: 700,
      }}>AUTO</span>
    </span>
  );
}

// ── 스타일 ────────────────────────────────────────────────────
const S = {
  page:      { padding: 24, fontFamily: '"맑은 고딕", sans-serif', maxWidth: 1200, margin: "0 auto" },
  header:    { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 10 },
  title:     { fontSize: 20, fontWeight: 700, margin: 0 },
  sheetRow:  { display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", borderRadius: 8 },
  badge:     { color: "#fff", borderRadius: 20, padding: "2px 12px", fontSize: 12, fontWeight: 600 },
  body:      { display: "flex", gap: 20, alignItems: "flex-start" },
  leftCol:   { flex: 1, minWidth: 0 },
  rightCol:  { width: 460, flexShrink: 0 },
  card:      { background: "#fff", border: "1px solid #e0e0e0", borderRadius: 12, padding: 20, marginBottom: 16 },
  cardTitle: { fontSize: 15, fontWeight: 700, marginBottom: 14 },
  input:     { padding: "8px 10px", border: "1px solid #ccc", borderRadius: 6, fontSize: 13, width: "100%", boxSizing: "border-box", fontFamily: '"맑은 고딕", sans-serif' },
  autoInput: { background: "#f0f7f0", borderColor: "#a5d6a7" },
  autoWrap:  { position: "relative" },
  autoTag:   { position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", fontSize: 9, color: "#2E7D32", fontWeight: 700, pointerEvents: "none" },
  grid2:     { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" },
  preview:   { marginTop: 16, background: "#f8f9fa", borderRadius: 8, padding: "12px 14px" },
  previewTitle: { fontSize: 12, fontWeight: 700, color: "#888", marginBottom: 8 },
  previewRow:   { display: "flex", gap: 8, fontSize: 13, marginBottom: 5 },
  previewKey:   { color: "#666", fontWeight: 600, width: 90, flexShrink: 0 },

  historyBox:   { marginTop: 16, background: "#E8F5E9", borderRadius: 8, padding: "12px 14px" },
  historyTitle: { fontSize: 12, fontWeight: 700, color: "#2E7D32", marginBottom: 8 },
  historyEmpty: { fontSize: 13, color: "#666", fontStyle: "italic" },
  historyTable: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
  th:           { padding: "4px 8px", background: "#C8E6C9", color: "#1B5E20", fontWeight: 700, textAlign: "left" },
  td:           { padding: "4px 8px", borderBottom: "1px solid #C8E6C9" },
  historyTotal: { display: "flex", justifyContent: "space-between", marginTop: 8, padding: "6px 8px", background: "#C8E6C9", borderRadius: 6, fontSize: 13 },

  formulaBox:   { background: "#F1F8E9", border: "1px dashed #A5D6A7", borderRadius: 8, padding: "10px 14px", fontSize: 12 },
  formulaTitle: { fontWeight: 700, color: "#33691E", marginBottom: 8 },
  formulaRow:   { display: "flex", gap: 12, alignItems: "center", marginBottom: 4, flexWrap: "wrap" },
  formulaLabel: { color: "#555", fontWeight: 600, width: 70, flexShrink: 0 },
  formulaNote:  { color: "#888", marginTop: 6 },

  roundPreview: { marginTop: 14, padding: "10px 14px", background: "#f0f4ff", borderRadius: 8, fontSize: 14 },
  errorBox:     { background: "#FFEBEE", border: "1px solid #FFCDD2", borderRadius: 8, padding: "10px 14px", color: "#C62828", marginTop: 12, fontSize: 13 },
  successBox:   { background: "#E8F5E9", border: "1px solid #C8E6C9", borderRadius: 8, padding: "10px 14px", color: "#1B5E20", marginTop: 12, fontSize: 13 },
  generateBtn:  { width: "100%", padding: "13px 0", border: "none", borderRadius: 8, color: "#fff", fontSize: 15, fontWeight: 700, marginTop: 16, cursor: "pointer" },
  hint:         { fontSize: 11, color: "#aaa", textAlign: "center", margin: "8px 0 0" },

  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" },
  modalBox:     { background: "#fff", borderRadius: 16, padding: "32px 36px", minWidth: 340, maxWidth: 420, textAlign: "center", boxShadow: "0 8px 32px rgba(0,0,0,0.18)" },
  modalIcon:    { fontSize: 48, marginBottom: 12 },
  modalTitle:   { fontSize: 20, fontWeight: 700, marginBottom: 14 },
  modalSheets:  { display: "flex", justifyContent: "center", gap: 6, flexWrap: "wrap", marginBottom: 16 },
  modalMsg:     { fontSize: 14, color: "#444", marginBottom: 8 },
  modalRefresh: { fontSize: 13, color: "#E65100", background: "#FFF3E0", borderRadius: 8, padding: "8px 12px", marginTop: 4 },
};